// Office Subcontractor detail page. Contact (inline edit), insurance (read-only with a
// below-minimum warning), onboarding (resend / approve / reject), agreement (send / resend
// / download), certificates and insurance (add / edit / upload / download / delete, Session
// AEL D37) and linked projects.
//
// SESSION AUV, design system Session 8 (People): RecordHeader (the status tags sit beside the title), StatusPill tags
// read from the registry (the page's own words kept: see subcontractorsUi.tsx), Button for the primary actions
// (Approve, Send Agreement, the two Saves, the certificate and upload dialogs, Restore, Archive subcontractor), Loader,
// and lib/format dates (both local formatters are gone: a date prints "5 Oct 2026", and the certificate expiry that
// printed "1 September 2026" prints "1 Sep 2026"). The neutral outline buttons, the amber alert panels and the
// admin-only rate card, payment details and portal access sections are untouched in behaviour. Presentation only.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import type {
  SubcontractorDetail,
  SubcontractorProjectLink,
  SubcontractorUpdateInput,
  SubcontractorCertificate,
  SubcontractorCertificateInput,
  SubCertType,
} from "../../lib/api";
import {
  StatusTag,
  AgreementTag,
  SubcontractorArchiveDialog,
  SubcontractorDeleteDialog,
  CertStatusTag,
  CERT_TYPE_LABELS,
  CERT_TYPE_OPTIONS,
} from "./subcontractorsUi";
import { CommunicationTimeline } from "./CommunicationTimeline";
import { ReportDownloadButton } from "./ReportDownloadButton";
import { copyText } from "../../lib/clipboard";
import { useSingleTimeout } from "../../lib/useSingleTimeout";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
// Session ARZ: payment (bank) details, its own file.
import { SubcontractorPaymentDetailsSection } from "./SubcontractorPaymentDetailsSection";
import { XeroContactButton } from "./SubcontractorXeroContact";
import { SubcontractorPortalAccessSection } from "./SubcontractorPortalAccessSection";
import { SubcontractorEngineersSection } from "./SubcontractorEngineersSection";
import { SubcontractorRateCardSection } from "./SubcontractorRateCardSection";
import { SubcontractorWelcomePackSection } from "./SubcontractorWelcomePackSection";
import { SubcontractorInvoiceStatusSection } from "./SubcontractorInvoiceStatusSection";
import { SubcontractorXeroPurchaseOrdersSection } from "./SubcontractorXeroPurchaseOrdersSection";
import { RejectDocumentModal } from "./SubcontractorDocumentReviewPage";
import { useAuth } from "../../lib/auth";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { ContactValue } from "../../components/ContactValue";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { formatDate, formatDateLong } from "../../lib/format";
import type { InviteNavState, RateCardApprovedWeek, SubcontractorStatusHistory, SubcontractorType, SubcontractorTypeInput } from "../../lib/api";
import { londonToday } from "../../lib/rateCardUi";
import { KindCards, LabourOptions, SUPPLY_AND_FIT_NOTE, type TypeChoiceValue } from "./SubcontractorTypeChoice";

// Insurance levels are stored in full pounds; the office displays them in millions.
function millions(n: number | null): string {
  if (n == null) return "Not yet provided";
  const m = n / 1_000_000;
  return `£${Number.isInteger(m) ? m : m.toFixed(2)} million`;
}

export function SubcontractorDetailPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { id = "" } = useParams();
  const [sub, setSub] = useState<SubcontractorDetail | null>(null);
  // Session AEH B3: a failed projects fetch used to resolve to a silently empty list with no way
  // to tell "not linked to any projects" apart from "the load failed". useOptionsLoader keeps the
  // last-known data, surfaces the failure and gives the office a Retry.
  const { data: projectsData, error: projectsError, retry: retryProjects } = useOptionsLoader<SubcontractorProjectLink[]>(
    () => api.subcontractors.projects(id),
    [id],
  );
  const projects = projectsData ?? [];
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Session ADU: invite honesty. Session RC2: the invite is a Gmail DRAFT the office sends from Gmail, so the page says
  // where it is waiting, when its link stops working, and (on a redraft) that an earlier draft's link is dead.
  // inviteWarning is the reason no draft was made (or that one may exist), shown with the copyable link: either this page
  // was reached from an "Add" whose draft failed (navigation state from SubcontractorsPage), or a Redraft returned
  // drafted:false. actionNotice carries the positive confirmation.
  const location = useLocation();
  const arrivedWith = (location.state as { invite?: InviteNavState } | null)?.invite ?? null;
  const [inviteWarning, setInviteWarning] = useState<string | null>(
    arrivedWith && !arrivedWith.drafted ? `${arrivedWith.error ?? "No draft was made"}. Use the copyable registration link below to send it another way.` : null,
  );
  const [actionNotice, setActionNotice] = useState<string | null>(
    arrivedWith?.drafted ? `The invitation is waiting in Gmail Drafts for you to send. Its link works until ${formatDate(arrivedWith.expiresAt)}.` : null,
  );

  // Bumped when the Accounting card writes to Xero, so the Payment details panel re-reads its "sent to Xero" state.
  const [paymentsReload, setPaymentsReload] = useState(0);
  const load = useCallback(() => {
    setLoadError(false);
    api.subcontractors
      .get(id)
      .then(setSub)
      .catch(() => setLoadError(true));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setActionError(null);
      try {
        await fn();
        load();
      } catch (e) {
        setActionError(friendlyApiError(e, "That action could not be completed."));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  // Redraft bypasses the generic runAction because it must inspect the resolved { drafted } flag: a 200
  // with drafted:false is a successful HTTP call but no draft, which runAction (which only
  // surfaces a throw) would read as success. This is the same pattern the quote send path uses.
  const resendInvite = useCallback(
    async (subId: string) => {
      setBusy(true);
      setActionError(null);
      setActionNotice(null);
      try {
        const res = await api.subcontractors.resendInvite(subId);
        load();
        if (res.drafted) {
          setInviteWarning(null);
          setActionNotice(`A new invitation is waiting in Gmail Drafts for you to send. Its link works until ${formatDate(res.inviteLinkExpiresAt)}. ${res.earlierDraftNote}.`);
        } else {
          setInviteWarning(`${res.inviteDraftError ?? "No draft was made"}. Use the copyable registration link below to send it another way. ${res.earlierDraftNote}.`);
        }
      } catch (e) {
        setActionError(friendlyApiError(e, "That action could not be completed."));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  if (loadError) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <Link to="/office/subcontractors" className="font-menu text-xs tracking-wide text-ele-blue hover:underline">Back to subcontractors</Link>
        <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">Could not load this subcontractor.</p>
      </div>
    );
  }
  if (!sub) return <Loader label="Loading the subcontractor..." shape="panel" className="p-6" />;

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6">
      <RecordHeader
        className="mb-6"
        title={sub.company}
        backTo="/office/subcontractors"
        backLabel="Back to subcontractors"
        status={
          <>
            <StatusPill domain="subcontractorEngagement" status={sub.subcontractorStatus ?? "not_set"} />
            <StatusTag status={sub.status} />
            <AgreementTag status={sub.agreementStatus} />
            {sub.archivedAt && <StatusPill word="Archived" tone="grey" />}
            {/* Session RC1: the type and the Technician tick. The card (Standard vN or Custom) is on the rate card, admin only. */}
            {sub.kind === "LABOUR" && <StatusPill word="Labour" tone="blue" />}
            {sub.kind === "SUPPLY_AND_FIT" && <StatusPill word="Supply and fit" tone="blue" />}
            {sub.technicianDefault && <StatusPill word="Technician" tone="green" />}
          </>
        }
        subtitle={sub.trade || "Trade not set"}
        actions={
          <ReportDownloadButton
            reportType="SubcontractorComplianceFile"
            parameters={{ subcontractorId: sub.id }}
            label="Download Compliance File"
            className="rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs uppercase tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-60 max-sm:min-h-11"
          />
        }
      />

      {actionError && <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{actionError}</p>}
      {inviteWarning && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {inviteWarning}
        </p>
      )}
      {actionNotice && <p className="mb-4 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">{actionNotice}</p>}

      {/* Session RC1: a subcontractor registered before the type existed is asked for it, calmly, once. */}
      {sub.kind == null && !sub.archivedAt && <SubcontractorTypeNotSet sub={sub} isAdmin={isAdmin} onSaved={setSub} />}
      <OnboardingSection sub={sub} busy={busy} runAction={runAction} resendInvite={resendInvite} />
      <SubcontractorStatusSection sub={sub} onSaved={setSub} />
      {/* Session AUU: the rate card the pay check figure is made from. ADMIN ONLY, the check figure's own access pattern.
          Session RC1: a supply and fit company has no rate card, no agreement and no welcome pack: it is paid on purchase
          orders against its own quotes, and its Xero bills show below. */}
      {isAdmin && sub.kind !== "SUPPLY_AND_FIT" && <SubcontractorRateCardSection subId={sub.id} />}
      {/* Session RC2: the company's Xero purchase orders in place of the rate card. ADMIN ONLY (it carries what ELE pays);
          an office user sees no card at all. */}
      {isAdmin && sub.kind === "SUPPLY_AND_FIT" && <SubcontractorXeroPurchaseOrdersSection subId={sub.id} />}
      <InductionResultsSection sub={sub} />
      {sub.kind !== "SUPPLY_AND_FIT" && <AgreementSection sub={sub} busy={busy} runAction={runAction} onNotice={setActionNotice} />}
      {/* Session AUY: the welcome pack (the V2 agreement, Your Rates, FAQ, Getting Set Up) and its Gmail draft. ADMIN ONLY
          (AUY fix-up, defect 4): Schedule 1 and Your Rates state every rate, so the section follows the rate card's gate
          and the server refuses the documents and the preparation to anyone else. */}
      {isAdmin && sub.status === "active" && sub.kind !== "SUPPLY_AND_FIT" && <SubcontractorWelcomePackSection subId={sub.id} />}
      {/* Session AVB: what the subcontractor sees about their invoices, read from Xero bills. Admin only (the AUU rule). */}
      {isAdmin && <SubcontractorInvoiceStatusSection subId={sub.id} />}
      <ContactSection sub={sub} onSaved={load} />
      <InsuranceSection sub={sub} onSaved={load} />
      <SubcontractorPaymentDetailsSection sub={sub} onChanged={load} reloadKey={paymentsReload} />
      <AccountingSection
        sub={sub}
        onLinked={() => {
          load();
          setPaymentsReload((n) => n + 1);
        }}
      />
      <CertificatesSection subId={sub.id} onStatusChanged={load} />
      <SubcontractorEngineersSection subId={sub.id} company={sub.company} archived={!!sub.archivedAt} />
      <SubcontractorPortalAccessSection subId={sub.id} archived={!!sub.archivedAt} />

      <section className="mb-5">
        <h2 className="mb-3 font-heading text-lg uppercase tracking-wide text-ele-blue">Communication</h2>
        <CommunicationTimeline target={{ subcontractorId: sub.id }} />
      </section>

      <ProjectsSection projects={projects} error={projectsError} onRetry={retryProjects} />

      {sub.status === "active" && !sub.hasSignedAgreement && !sub.archivedAt && (
        <button
          onClick={() => runAction(() => api.subcontractors.update(id, { action: "deactivate" }))}
          disabled={busy}
          className="mt-2 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-danger disabled:opacity-50"
        >
          Deactivate
        </button>
      )}

      <SubcontractorOffboarding sub={sub} onChanged={load} />
    </div>
  );
}

// Session RC1: "Type not set". The same two-card choice the Add subcontractor modal opens with; the save previews first
// (PUT /:id/type with preview: true) and shows the server's own summary before anything is written.
function SubcontractorTypeNotSet({ sub, isAdmin, onSaved }: { sub: SubcontractorDetail; isAdmin: boolean; onSaved: (s: SubcontractorDetail) => void }) {
  const today = useMemo(() => londonToday(), []);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<TypeChoiceValue>({ kind: null, rateCardSource: "STANDARD", technician: sub.technicianDefault ?? false, effectiveFrom: today });
  const [summary, setSummary] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function body(): SubcontractorTypeInput | null {
    if (!value.kind) return null;
    if (value.kind === "SUPPLY_AND_FIT") return { kind: "SUPPLY_AND_FIT" };
    return { kind: "LABOUR", rateCardSource: value.rateCardSource, effectiveFrom: value.effectiveFrom, technician: value.technician };
  }
  function change(next: TypeChoiceValue) {
    setValue(next);
    setSummary(null);
    setErr(null);
  }
  async function review() {
    const b = body();
    if (!b) return;
    if (b.effectiveFrom && b.effectiveFrom < today) {
      setErr("The rates cannot start in the past. Choose today or a later date.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      setSummary((await api.subcontractors.previewType(sub.id, b)).summary);
    } catch (e) {
      setErr(friendlyApiError(e, "Could not check what this would do. Please try again."));
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    const b = body();
    if (!b) return;
    setBusy(true);
    setErr(null);
    try {
      onSaved(await api.subcontractors.setType(sub.id, b));
    } catch (e) {
      setErr(friendlyApiError(e, "Could not set the type. Please try again."));
      setBusy(false);
    }
  }

  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Type not set">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg uppercase tracking-wide text-ele-blue">Type not set</h2>
          <p className="mt-1 text-sm text-ele-body">Say whether this is a labour subcontractor or a supply and fit company, so they are paid the right way.</p>
        </div>
        {!open && (
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            Set the type
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-3">
          <KindCards value={value.kind} onChange={(k) => change({ ...value, kind: k })} disabled={busy} />
          {value.kind === "LABOUR" && <LabourOptions value={value} onChange={change} isAdmin={isAdmin} today={today} disabled={busy} />}
          {value.kind === "SUPPLY_AND_FIT" && <p className="mt-3 text-sm text-ele-body">{SUPPLY_AND_FIT_NOTE}</p>}
          {summary && (
            <ul role="status" className="mt-3 list-disc space-y-1 rounded-ele border border-ele-purple-accent px-6 py-2 text-sm text-ele-strong">
              {summary.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {err && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            {summary ? (
              <Button onClick={() => void save()} disabled={busy}>
                {busy ? "Saving..." : "Confirm and save"}
              </Button>
            ) : (
              <Button onClick={() => void review()} disabled={busy || !value.kind}>
                Review
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// Archive / Restore / Delete. Archive is the normal offboarding (soft, reversible). Delete is a
// permanent, less-prominent option shown only for a deletable record (no signed agreement, no
// linked projects). An archived record shows a Restore action instead.
function SubcontractorOffboarding({ sub, onChanged }: { sub: SubcontractorDetail; onChanged: () => void }) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<null | "archive" | "delete">(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Mirrors the server's subcontractorDeleteBlockers: a signed agreement, a linked project or (Session ARZ) payment details on
  // record each make this a record to archive, never to delete.
  const canDelete = sub.agreementStatus !== "signed" && sub.linkedProjectCount === 0 && !sub.hasPaymentDetailsRecord;

  async function doArchive() {
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.archive(sub.id);
      setDialog(null);
      onChanged();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not archive the subcontractor."));
      setDialog(null);
    } finally {
      // Reset busy on success too: after archiving, this component stays mounted and re-renders
      // into the archived branch, whose Restore button is disabled={busy}. Without this reset the
      // Restore control would stay stuck disabled ("Restoring...") until a manual page reload.
      setBusy(false);
    }
  }

  async function doRestore() {
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.restore(sub.id);
      onChanged();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not restore the subcontractor."));
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.remove(sub.id);
      navigate("/office/subcontractors");
    } catch (e) {
      setErr(friendlyApiError(e, "Could not delete the subcontractor."));
      setBusy(false);
      setDialog(null);
    }
  }

  return (
    <div className="mt-10 border-t border-ele-purple-accent/40 pt-4">
      {sub.archivedAt ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ele-faint">This subcontractor is archived. Their record, agreement and documents are retained.</p>
          <Button variant="secondary" size="sm" onClick={doRestore} disabled={busy}>
            {busy ? "Restoring..." : "Restore"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="accent" onClick={() => setDialog("archive")}>
            Archive subcontractor
          </Button>
          {canDelete && (
            <button
              onClick={() => setDialog("delete")}
              aria-label="Delete subcontractor"
              className="font-menu text-xs tracking-wide text-ele-danger hover:underline max-sm:min-h-11"
            >
              Delete
            </button>
          )}
        </div>
      )}
      {err && <p role="alert" className="mt-2 text-sm text-ele-danger">{err}</p>}
      {dialog === "archive" && <SubcontractorArchiveDialog busy={busy} onConfirm={doArchive} onCancel={() => setDialog(null)} />}
      {dialog === "delete" && <SubcontractorDeleteDialog busy={busy} onConfirm={doDelete} onCancel={() => setDialog(null)} />}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="mb-3 font-heading text-lg uppercase tracking-wide text-ele-blue">{title}</h2>
      {children}
    </section>
  );
}

function OnboardingSection({
  sub,
  busy,
  runAction,
  resendInvite,
}: {
  sub: SubcontractorDetail;
  busy: boolean;
  runAction: (fn: () => Promise<unknown>) => void;
  resendInvite: (subId: string) => void;
}) {
  if (sub.status === "active" || sub.status === "inactive") return null;
  return (
    <Card title="Onboarding">
      {sub.status === "pending_invite" ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ele-body">
              Awaiting registration. Invitation {sub.onboardingActive ? "active" : "expired"}
              {sub.onboardingTokenExpiresAt ? `, link expires ${formatDate(sub.onboardingTokenExpiresAt)}` : ""}.
            </p>
            <button onClick={() => resendInvite(sub.id)} disabled={busy} className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-green disabled:opacity-50">
              Redraft invite
            </button>
          </div>
          {sub.onboardingUrl ? (
            <OnboardingLinkRow url={sub.onboardingUrl} />
          ) : (
            <p className="text-xs text-ele-body opacity-70">The link has expired. Use Redraft invite to generate a fresh one.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ele-body">Registration submitted on {formatDate(sub.onboardingCompletedAt)}. Review the details and documents below, then approve.</p>
          <div className="flex gap-2">
            <Button onClick={() => runAction(() => api.subcontractors.update(sub.id, { action: "approve" }))} disabled={busy}>Approve</Button>
            <button onClick={() => runAction(() => api.subcontractors.update(sub.id, { action: "reject" }))} disabled={busy} className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-danger disabled:opacity-50">Reject</button>
          </div>
        </div>
      )}
    </Card>
  );
}

// The live onboarding link, displayed copyably so the office can send it by another channel
// (e.g. text) if the invite email did not arrive. The link is the subcontractor's bearer
// credential and is shown only to authenticated office/admin staff.
function OnboardingLinkRow({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const resetCopiedLater = useSingleTimeout();
  async function copy() {
    if (await copyText(url)) {
      setCopied(true);
      resetCopiedLater(() => setCopied(false), 2000);
    }
  }
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-base p-3">
      <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">Onboarding link</label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={url}
          aria-label="Onboarding link"
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field-inset px-2 py-1.5 text-xs text-ele-strong"
        />
        <button type="button" onClick={copy} className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-green">
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
      <p className="mt-1 text-xs text-ele-body opacity-70">Send this secure one-time link to the subcontractor another way if the invitation draft was not sent. It expires after 7 days.</p>
    </div>
  );
}

function AgreementSection({
  sub,
  busy,
  runAction,
  onNotice,
}: {
  sub: SubcontractorDetail;
  busy: boolean;
  runAction: (fn: () => Promise<unknown>) => void;
  // Session RC2 review: the page says what became of the covering note, never assumes it is in Gmail Drafts.
  onNotice: (s: string | null) => void;
}) {
  // Only meaningful once the subcontractor is active (approved).
  if (sub.status !== "active") return null;
  return (
    <Card title="Agreement">
      {sub.agreementStatus === "none" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ele-body">No agreement sent yet. Send the subcontractor agreement for signature: Signable emails it to them, and a covering note is drafted in Gmail for you to send (you are told if it could not be).</p>
          <Button
            onClick={() =>
              runAction(async () => {
                onNotice(null);
                const r = await api.subcontractors.sendAgreement(sub.id);
                onNotice(r.coveringDraftNote ? `Agreement sent for signature. ${r.coveringDraftNote}` : "Agreement sent for signature.");
              })
            }
            disabled={busy}
          >
            Send agreement
          </Button>
        </div>
      )}
      {sub.agreementStatus === "sent" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ele-body">Awaiting signature. Sent {formatDate(sub.agreementSentAt)}.</p>
          <button
            onClick={() =>
              runAction(async () => {
                onNotice(null);
                const r = await api.subcontractors.resendAgreement(sub.id);
                onNotice(r.coveringDraftNote ? `Agreement resent for signature. ${r.coveringDraftNote}` : "Agreement resent for signature.");
              })
            }
            disabled={busy} className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-green disabled:opacity-50">Resend</button>
        </div>
      )}
      {sub.agreementStatus === "signed" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ele-green">Signed on {formatDate(sub.agreementSignedAt)}.</p>
          {sub.agreementFileUrl && (
            <a href={sub.agreementFileUrl} target="_blank" rel="noopener noreferrer" className="font-menu text-xs tracking-wide text-ele-green hover:underline">Download signed agreement</a>
          )}
        </div>
      )}
    </Card>
  );
}

function ContactSection({ sub, onSaved }: { sub: SubcontractorDetail; onSaved: () => void }) {
  const [form, setForm] = useState({
    contactName: sub.contactName,
    email: sub.email,
    contactPhone: sub.contactPhone ?? "",
    address: sub.address ?? "",
    tradingAddress: sub.tradingAddress ?? "",
    companyNo: sub.companyNo ?? "",
    utrNumber: sub.utrNumber ?? "",
    vatNo: sub.vatNo ?? "",
    subcontractorType: (sub.subcontractorType ?? "") as "" | "bona_fide" | "labour_only",
    notes: sub.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body: SubcontractorUpdateInput = {
        contactName: form.contactName,
        email: form.email,
        contactPhone: form.contactPhone,
        address: form.address,
        tradingAddress: form.tradingAddress,
        companyNo: form.companyNo,
        utrNumber: form.utrNumber,
        vatNo: form.vatNo,
        notes: form.notes,
      };
      // The type enum only accepts the two values; never send "" (the server rejects it).
      if (form.subcontractorType === "bona_fide" || form.subcontractorType === "labour_only") {
        body.subcontractorType = form.subcontractorType;
      }
      await api.subcontractors.update(sub.id, body);
      setSaved(true);
      onSaved();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not save."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Contact">
      <div className="grid gap-3 sm:grid-cols-2">
        <Editable label="Contact name" value={form.contactName} onChange={(v) => set("contactName", v)} />
        <Editable label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" linkKind="email" />
        <Editable label="Phone" value={form.contactPhone} onChange={(v) => set("contactPhone", v)} linkKind="phone" />
        <div>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Subcontractor type</label>
          <select
            value={form.subcontractorType}
            aria-label="Subcontractor type"
            onChange={(e) => set("subcontractorType", e.target.value as "" | "bona_fide" | "labour_only")}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
          >
            <option value="">Not set</option>
            <option value="bona_fide">Bona-fide</option>
            <option value="labour_only">Labour-only</option>
          </select>
        </div>
        {/* The identifier follows the type: a bona-fide subcontractor is a company (company
            number); a labour-only one is a sole trader (UTR). When the type is not yet set,
            show both so the office can enter whichever applies. */}
        {form.subcontractorType !== "labour_only" && (
          <Editable label="Company number" value={form.companyNo} onChange={(v) => set("companyNo", v)} />
        )}
        {form.subcontractorType !== "bona_fide" && (
          <Editable label="UTR number" value={form.utrNumber} onChange={(v) => set("utrNumber", v)} />
        )}
        <Editable label="VAT number" value={form.vatNo} onChange={(v) => set("vatNo", v)} />
        <Editable label="Registered address" value={form.address} onChange={(v) => set("address", v)} />
        <Editable label="Trading address" value={form.tradingAddress} onChange={(v) => set("tradingAddress", v)} />
      </div>
      <div className="mt-3">
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Notes</label>
        <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
      </div>
      {err && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={save} disabled={busy}>{busy ? "Saving..." : "Save"}</Button>
        {saved && <span className="font-menu text-xs uppercase tracking-wide text-ele-green">Saved</span>}
      </div>
    </Card>
  );
}

// Session AUR: the induction no longer blocks a registration, so it reads inductionPassedAt (back-filled for every
// registration before AUR, when it was mandatory), never onboardingCompletedAt.
function InductionResultsSection({ sub }: { sub: SubcontractorDetail }) {
  return (
    <Card title="Induction">
      {sub.inductionPassedAt ? (
        <p className="text-sm text-ele-green">
          Induction completed and passed on {formatDate(sub.inductionPassedAt)} (all questions answered correctly).
        </p>
      ) : sub.onboardingCompletedAt ? (
        <p className="text-sm text-ele-body">
          Registration submitted without the induction completed. Go through the questions with the subcontractor.
        </p>
      ) : (
        <p className="text-sm text-ele-body">Induction not yet completed.</p>
      )}
    </Card>
  );
}

// Session AUR: the status, Bona Fide or Labour Only, which the office owns. A new registration records it from the
// certificates attached (Labour Only when they are missing; Bona Fide pending approval when they are there). Every
// subcontractor registered before AUR reads "Status not set" until the office sets it. Saving always writes an audit
// entry on the server; confirming Bona Fide here is what approves a pending one.
const ENGAGEMENT_CHOICES: Array<{ value: "" | SubcontractorType; label: string }> = [
  { value: "bona_fide", label: "Bona Fide" },
  { value: "labour_only", label: "Labour Only" },
  { value: "", label: "Not set" },
];

function engagementExplanation(sub: SubcontractorDetail): string {
  switch (sub.subcontractorStatus ?? "not_set") {
    case "bona_fide_pending":
      return "The sign-up form recorded Bona Fide because insurance certificates were attached. Those documents are unverified: check them below, then confirm Bona Fide or set Labour Only.";
    case "not_set":
      return "No status is recorded. Choose Bona Fide or Labour Only.";
    case "labour_only":
      return sub.subcontractorStatusSetBy === "the sign-up form"
        ? "Recorded as Labour Only at sign-up because the required insurance certificates were not attached. Change it once they are received and checked."
        : "Works under ELE's direction and is covered by ELE's insurance.";
    default:
      return "Carries their own Public Liability insurance, and Employers Liability where they employ anyone.";
  }
}

function SubcontractorStatusSection({ sub, onSaved }: { sub: SubcontractorDetail; onSaved: (s: SubcontractorDetail) => void }) {
  const current: "" | SubcontractorType =
    sub.subcontractorStatus === "labour_only" ? "labour_only" : sub.subcontractorStatus === "bona_fide" || sub.subcontractorStatus === "bona_fide_pending" ? "bona_fide" : "";
  const [choice, setChoice] = useState<"" | SubcontractorType>(current);
  // Follow the saved value (after Approve, or a save from elsewhere).
  useEffect(() => setChoice(current), [current]);
  // Session AUZ: the status applies FROM A DATE (today by default, never the future). The save previews the already
  // approved weeks the Labour Only deduction would move and asks for a confirmation before it goes ahead.
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirm, setConfirm] = useState<{ value: "" | SubcontractorType; weeks: RateCardApprovedWeek[]; message: string } | null>(null);
  const [history, setHistory] = useState<SubcontractorStatusHistory | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const pending = sub.subcontractorStatus === "bona_fide_pending";

  const loadHistory = useCallback(() => {
    api.subcontractors.statusHistory(sub.id).then(setHistory, () => setHistoryError("Could not load the status history."));
  }, [sub.id]);
  useEffect(() => loadHistory(), [loadHistory, sub.subcontractorStatus, sub.subcontractorStatusSetAt]);

  async function save(value: "" | SubcontractorType, affectsApprovedWeeks = false) {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const status = value === "" ? null : value;
      if (!affectsApprovedWeeks) {
        const pv = await api.subcontractors.previewSubcontractorStatus(sub.id, status, effectiveFrom || undefined);
        if (pv.approvedWeeksMoved.length > 0) {
          setConfirm({ value, weeks: pv.approvedWeeksMoved, message: pv.message });
          return;
        }
      }
      const next = await api.subcontractors.setSubcontractorStatusFrom(sub.id, status, { effectiveFrom: effectiveFrom || undefined, affectsApprovedWeeks });
      onSaved(next);
      setConfirm(null);
      setSaved(true);
      loadHistory();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not save the status."));
    } finally {
      setBusy(false);
    }
  }

  const employsText = sub.employsStaff == null ? "Not answered" : sub.employsStaff ? "Yes" : "No";
  return (
    <Card title="Status">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <StatusPill domain="subcontractorEngagement" status={sub.subcontractorStatus ?? "not_set"} />
        {sub.subcontractorStatusSetAt && sub.subcontractorStatusSetBy && (
          <span className="text-xs text-ele-body">
            Set by {sub.subcontractorStatusSetBy} on {formatDate(sub.subcontractorStatusSetAt)}
          </span>
        )}
      </div>
      <p className="mb-2 text-sm text-ele-body">{engagementExplanation(sub)}</p>
      <p className="mb-2 text-xs text-ele-body">
        The status applies from a date and its history is kept: the timesheet check figure prices the Labour Only deduction from the status in force on each day.
        Approving the required insurance certificates below moves it to Bona Fide from that day (clause 12.5 of the agreement), never for earlier days.
      </p>
      {sub.onboardingCompletedAt && <p className="mb-3 text-xs text-ele-body">Employs anyone (from the registration): {employsText}</p>}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="subcontractor-status" className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Set status</label>
          <select
            id="subcontractor-status"
            value={choice}
            onChange={(e) => {
              setChoice(e.target.value as "" | SubcontractorType);
              setSaved(false);
            }}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
          >
            {ENGAGEMENT_CHOICES.map((c) => (
              <option key={c.value || "none"} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="subcontractor-status-from" className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Applies from</label>
          <input
            id="subcontractor-status-from"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
            aria-describedby="subcontractor-status-from-help"
          />
        </div>
        <Button size="sm" variant="secondary" loading={busy} onClick={() => save(choice)}>
          Save status
        </Button>
        {pending && (
          <Button size="sm" variant="primary" loading={busy} onClick={() => save("bona_fide")}>
            Approve Bona Fide
          </Button>
        )}
        {saved && <span className="font-menu text-xs uppercase tracking-wide text-ele-green">Saved</span>}
      </div>
      <p id="subcontractor-status-from-help" className="mt-1 text-xs text-ele-body">Leave the date blank for today. A future date is refused.</p>
      {err && <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
      {confirm && (
        <div role="alertdialog" aria-label="Approved weeks affected" className="mt-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-3 text-sm text-ele-strong">
          <p>{confirm.message}</p>
          <ul className="mt-2 list-disc pl-5 text-ele-body">
            {confirm.weeks.map((w) => (
              <li key={`${w.engineerId}-${w.weekStarting}`}>
                {w.engineerName}, week beginning {formatDateLong(w.weekStarting)}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="primary" loading={busy} onClick={() => save(confirm.value, true)}>
              Confirm and reprice those weeks
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <StatusHistoryList subId={sub.id} history={history} error={historyError} onChanged={() => { loadHistory(); api.subcontractors.get(sub.id).then(onSaved).catch(() => undefined); }} />
    </Card>
  );
}

// Session AUZ: the status history, with the office's corrections (re-date, remove) behind the same approved-weeks guard.
function StatusHistoryList({ subId, history, error, onChanged }: { subId: string; history: SubcontractorStatusHistory | null; error: string | null; onChanged: () => void }) {
  const [redating, setRedating] = useState<{ id: string; date: string } | null>(null);
  const [removing, setRemoving] = useState<SubcontractorStatusHistory["periods"][number] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingWeeks, setPendingWeeks] = useState<RateCardApprovedWeek[] | null>(null);

  async function redate(affectsApprovedWeeks = false) {
    if (!redating) return;
    setBusy(true);
    setErr(null);
    try {
      if (!affectsApprovedWeeks) {
        const pv = await api.subcontractors.redateStatusPeriod(subId, redating.id, redating.date, { preview: true });
        if ("preview" in pv && pv.approvedWeeksMoved.length > 0) {
          setPendingWeeks(pv.approvedWeeksMoved);
          return;
        }
      }
      await api.subcontractors.redateStatusPeriod(subId, redating.id, redating.date, { affectsApprovedWeeks });
      setRedating(null);
      setPendingWeeks(null);
      onChanged();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not change the date."));
    } finally {
      setBusy(false);
    }
  }

  async function remove(affectsApprovedWeeks = false) {
    if (!removing) return;
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.removeStatusPeriod(subId, removing.id, { affectsApprovedWeeks });
      setRemoving(null);
      setPendingWeeks(null);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.error === "approved_weeks_affected") {
        setPendingWeeks((e.body.weeks as RateCardApprovedWeek[] | undefined) ?? []);
        setErr(e.message);
      } else {
        setErr(friendlyApiError(e, "Could not remove the entry."));
      }
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p className="mt-3 text-xs text-ele-danger">{error}</p>;
  if (!history) return <Loader label="Loading the status history..." />;
  return (
    <div className="mt-4">
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Status history</p>
      {history.periods.length === 0 ? (
        <p className="mt-1 text-xs text-ele-body">No status has been recorded yet.</p>
      ) : (
        <ul className="mt-1 divide-y divide-ele-purple-accent/40 text-sm">
          {history.periods.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div>
                <span className="text-ele-strong">{p.stateLabel}</span>
                <span className="text-ele-body"> from {p.effectiveFromLabel}</span>
                <span className="block text-xs text-ele-body">
                  Recorded by {p.recordedBy} ({p.sourceLabel}), {formatDate(p.recordedAt)}
                </span>
                {redating?.id === p.id && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <input type="date" value={redating.date} onChange={(e) => setRedating({ id: p.id, date: e.target.value })} className="rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" aria-label="Corrected date" />
                    <Button size="sm" variant="primary" loading={busy} onClick={() => redate(false)}>
                      Save date
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => { setRedating(null); setPendingWeeks(null); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setRedating({ id: p.id, date: p.effectiveFrom }); setPendingWeeks(null); setErr(null); }}>
                  Correct date
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setRemoving(p); setPendingWeeks(null); setErr(null); }}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {err && <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
      {pendingWeeks && pendingWeeks.length > 0 && (redating || removing) && (
        <div role="alertdialog" aria-label="Approved weeks affected" className="mt-2 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-3 text-sm text-ele-strong">
          <p>This correction moves the Labour Only deduction on already approved weeks:</p>
          <ul className="mt-2 list-disc pl-5 text-ele-body">
            {pendingWeeks.map((w) => (
              <li key={`${w.engineerId}-${w.weekStarting}`}>
                {w.engineerName}, week beginning {formatDateLong(w.weekStarting)}
              </li>
            ))}
          </ul>
          <Button size="sm" variant="primary" className="mt-2" loading={busy} onClick={() => (redating ? redate(true) : remove(true))}>
            Confirm and reprice those weeks
          </Button>
        </div>
      )}
      {removing && (
        <ConfirmDeleteModal
          open
          variant="destructive"
          title="Remove this status entry?"
          message={`${removing.stateLabel} from ${removing.effectiveFromLabel}, recorded by ${removing.recordedBy}. The status in force then follows the entries that remain. This is audited.`}
          confirmLabel="Remove entry"
          busy={busy}
          onCancel={() => { setRemoving(null); setPendingWeeks(null); }}
          onConfirm={() => remove(pendingWeeks != null && pendingWeeks.length > 0)}
        />
      )}
    </div>
  );
}

function InsuranceBox({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-base px-3 py-2">
      <div className="font-menu text-[10px] uppercase tracking-wide text-ele-blue">{label}</div>
      <div className={`mt-0.5 ${danger ? "text-ele-danger" : "text-ele-strong"}`}>{value}</div>
    </div>
  );
}

function InsuranceSection({ sub, onSaved }: { sub: SubcontractorDetail; onSaved: () => void }) {
  const typeText = sub.subcontractorType === "labour_only" ? "Labour-only" : sub.subcontractorType === "bona_fide" ? "Bona-fide" : "Not yet declared";
  return (
    <Card title="Insurance">
      {/* Session AUR: this is the type recorded at registration, which the agreement's insurance clause reads; the
          office-owned status is the Status card above. The levels shown are as stated, not verified. */}
      <p className="mb-3 font-menu text-[10px] uppercase tracking-wide text-ele-blue">
        Type recorded at registration: <span className="text-ele-strong">{typeText}</span>
      </p>
      {sub.subcontractorType === "labour_only" ? (
        <LabourOnlyInsurance sub={sub} onSaved={onSaved} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <InsuranceBox label="Public Liability held" value={millions(sub.publicLiabilityHeld)} danger={sub.publicLiabilityBelowMin} />
            <InsuranceBox label="Employer Liability held" value={millions(sub.employerLiabilityHeld)} danger={sub.employerLiabilityBelowMin} />
          </div>
          <p className="mt-3 text-xs text-ele-body opacity-70">
            ELE requirement: {millions(sub.publicLiabilityMinimum)} Public Liability / {millions(sub.employerLiabilityMinimum)} Employer Liability.
            These are the levels the subcontractor stated; they are not evidence of cover until the certificates are verified.
          </p>
          {(sub.publicLiabilityBelowMin || sub.employerLiabilityBelowMin) && (
            <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-red-500/15 px-3 py-2 text-sm text-ele-danger">
              A stated insurance level is below the ELE minimum. Confirm cover before engaging this subcontractor.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

// Push the subcontractor to Xero as a payable contact (a supplier is created in Xero when the
// first bill is raised against the contact by hand). This creates the contact only, never a
// transaction. Idempotent server-side, so a second click never duplicates the contact.
function AccountingSection({ sub, onLinked }: { sub: SubcontractorDetail; onLinked: () => void }) {
  // Session ARZ, Item 4: one preview-then-confirm flow for a new contact and an existing one alike. The preview shows
  // what Xero holds beside what would be sent (bank details masked), and nothing is written until the office confirms.
  return (
    <Card title="Accounting">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {sub.xeroContactId ? (
          <p className="max-w-xl text-sm text-ele-body">
            <span className="mr-2 font-menu text-xs uppercase tracking-wide text-ele-green">Linked to Xero</span>
            Raise bills against them in Xero as normal. Check or update the contact (VAT number, verified bank details,
            expense account and bill terms) at any time.
          </p>
        ) : (
          <p className="max-w-xl text-sm text-ele-body">
            Add this subcontractor to Xero as a payable contact, with their VAT number, verified bank details, expense
            account and bill terms, so you can enter and pay their bills in Xero. No bill or payment is created.
          </p>
        )}
        <XeroContactButton subId={sub.id} label={sub.xeroContactId ? "Update Xero contact" : "Add to Xero"} primary={!sub.xeroContactId} onDone={onLinked} />
      </div>
    </Card>
  );
}

function LabourOnlyInsurance({ sub, onSaved }: { sub: SubcontractorDetail; onSaved: () => void }) {
  const [spend, setSpend] = useState(sub.expectedAnnualSpend == null ? "" : String(sub.expectedAnnualSpend));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const v = spend.trim() === "" ? null : Number(spend);
    if (v != null && (!Number.isFinite(v) || v < 0)) {
      setErr("Enter a valid amount in pounds.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.update(sub.id, { expectedAnnualSpend: v });
      setSaved(true);
      onSaved();
    } catch (e) {
      setErr(friendlyApiError(e, "Could not save."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="text-sm text-ele-body">Labour-only: covered under ELE's policy.</p>
      <div className="mt-3 max-w-xs">
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Expected annual spend (£)</label>
        <input
          type="number"
          inputMode="numeric"
          value={spend}
          aria-label="Expected annual spend"
          onChange={(e) => {
            setSpend(e.target.value);
            setSaved(false);
          }}
          className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
        />
        <p className="mt-1 text-xs text-ele-body opacity-70">
          ELE's Chubb policy has a declared labour-only spend. Track spend per subcontractor to manage your annual declaration.
        </p>
      </div>
      {err && <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={save} disabled={busy}>{busy ? "Saving..." : "Save"}</Button>
        {saved && <span className="font-menu text-xs uppercase tracking-wide text-ele-green">Saved</span>}
      </div>
    </>
  );
}

// A certificate IS a SubcontractorDoc row (see the model comment in prisma/schema.prisma):
// this section reads and writes the same rows the upload path creates, with the structured
// type, issuer and reference number, and a status derived server-side. It REPLACES the old
// "Compliance documents" section (the two were the same rows shown two different ways).
// A date input (type="date") wants a plain yyyy-mm-dd value; the server never returns fileUrl
// (see the DTO comment below), so there is nothing to prefill for that field.
function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function issuerRefText(c: SubcontractorCertificate): string {
  const parts: string[] = [];
  if (c.issuer) parts.push(c.issuer);
  if (c.referenceNumber) parts.push(`Ref: ${c.referenceNumber}`);
  return parts.length > 0 ? parts.join(" · ") : "-";
}

function certDeleteMessage(c: SubcontractorCertificate): string {
  const name = c.name && c.name.trim().length > 0 ? c.name : c.typeLabel;
  const expiry = c.expiryDate ? `, expiring ${formatDate(c.expiryDate)}` : "";
  return `This will permanently delete "${name}" (${c.typeLabel})${expiry}. This cannot be undone.`;
}

function CertificatesSection({ subId, onStatusChanged }: { subId: string; onStatusChanged?: () => void }) {
  const { data: certsData, error, retry } = useOptionsLoader<SubcontractorCertificate[]>(
    () => api.subcontractors.certificates(subId).then((r) => r.certificates),
    [subId],
  );
  const certs = certsData ?? [];
  const needsAttention = certs.some((c) => c.status === "expired" || c.status === "expiring");
  const unverifiedCount = certs.filter((c) => !c.verified).length;

  const [formTarget, setFormTarget] = useState<null | "add" | SubcontractorCertificate>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SubcontractorCertificate | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Session AUZ: the review flow. Approve stamps the document verified (and may move the status to Bona Fide, clause
  // 12.5, which the note from the server says); Reject asks for a reason the subcontractor reads on the portal.
  const [reviewBusy, setReviewBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<SubcontractorCertificate | null>(null);
  const submittedCount = certs.filter((c) => c.reviewState === "submitted" && c.submittedViaPortal).length;

  async function approve(c: SubcontractorCertificate) {
    setReviewBusy(c.id);
    setNotice(null);
    try {
      const res = await api.subcontractors.approveCertificate(subId, c.id);
      setNotice(`${c.typeLabel}${c.engineerName ? ` for ${c.engineerName}` : ""} approved. ${res.note}`);
      retry();
      if (res.statusMoved) onStatusChanged?.();
    } catch (e) {
      setNotice(friendlyApiError(e, "Could not approve the document."));
    } finally {
      setReviewBusy(null);
    }
  }

  // The two-step server contract: called with no token, deleteCertificate returns a
  // confirmation token (and summary) and deletes nothing; called again with that token it
  // performs the delete. Both calls happen behind the single branded "Delete" confirmation
  // below, so the office sees one two-step confirm while the server's token is still honoured.
  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const first = await api.subcontractors.deleteCertificate(subId, deleteTarget.id);
      if (first && "confirmationToken" in first) {
        await api.subcontractors.deleteCertificate(subId, deleteTarget.id, first.confirmationToken);
      }
      setDeleteTarget(null);
      retry();
    } catch (e) {
      // The token expires after 60 seconds (409 confirmation_expired); the server's message
      // tells the office to try again, and clicking Confirm again simply restarts the chain.
      setDeleteError(friendlyApiError(e, "Could not delete the certificate."));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <Card title="Certificates and Insurance">
      {needsAttention && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          One or more certificates require attention.
        </p>
      )}
      {notice && (
        <p className="mb-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">{notice}</p>
      )}
      {unverifiedCount > 0 && (
        <p className="mb-3 text-sm text-ele-body">
          {unverifiedCount === 1 ? "1 document is" : `${unverifiedCount} documents are`} unverified. An unverified document is not
          treated as compliant, or as evidence of insurance, until the office approves it.
          {submittedCount > 0 ? ` ${submittedCount === 1 ? "1 was" : `${submittedCount} were`} submitted on the Subcontractor Portal and ${submittedCount === 1 ? "is" : "are"} awaiting review.` : ""}
          {" "}Approving the required insurance moves the status to Bona Fide from that day (clause 12.5).
        </p>
      )}
      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFormTarget("add")}
          className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-green max-sm:min-h-11"
        >
          Add certificate
        </button>
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-green max-sm:min-h-11"
        >
          Upload document
        </button>
      </div>

      {error && certsData != null && (
        <p role="alert" className="mb-3 text-sm text-ele-danger">
          Could not refresh the certificate list.{" "}
          <button type="button" onClick={retry} className="underline hover:text-ele-strong">Retry</button>
        </p>
      )}

      {error && certsData == null ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load certificates.{" "}
          <button type="button" onClick={retry} className="underline hover:text-ele-strong">Retry</button>
        </p>
      ) : certsData == null ? (
        <Loader label="Loading the certificates..." />
      ) : certs.length === 0 ? (
        <p className="text-sm text-ele-body">No certificates recorded. Add the subcontractor's insurance and competency details above.</p>
      ) : (
        <div data-safetynet className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <th className="px-2 py-1">Type</th>
                <th className="max-md:hidden px-2 py-1">Name</th>
                <th className="max-md:hidden px-2 py-1">Issuer / Reference</th>
                <th className="px-2 py-1">Expiry</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id} className="border-t border-ele-purple-accent/60">
                  <td className="px-2 py-1.5 text-ele-strong">
                    {c.typeLabel}
                    {c.engineerName ? <span className="block text-xs text-ele-body">for {c.engineerName}</span> : null}
                    {c.submittedViaPortal ? <span className="block text-xs text-ele-body">Submitted on the portal</span> : null}
                    {c.reviewState === "rejected" && c.rejectionReason ? <span className="block text-xs text-ele-body">Not accepted: {c.rejectionReason}</span> : null}
                  </td>
                  <td className="max-md:hidden px-2 py-1.5 text-ele-body">{c.name || "-"}</td>
                  <td className="max-md:hidden px-2 py-1.5 text-ele-body">{issuerRefText(c)}</td>
                  <td className="px-2 py-1.5 text-ele-body">{formatDate(c.expiryDate)}</td>
                  <td className="px-2 py-1.5">
                    {/* Session AUR: an unverified certificate never shows as valid: "Unverified", with the expiry
                        reading beside it only when that is itself a warning (expiring or expired). */}
                    <span className="inline-flex flex-wrap gap-1">
                      {!c.verified && <StatusPill domain="documentVerification" status={c.reviewState === "rejected" ? "rejected" : c.submittedViaPortal ? "submitted" : "unverified"} />}
                      {(c.verified || c.status === "expired" || c.status === "expiring") && <CertStatusTag status={c.status} />}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-wrap gap-3">
                      {c.reviewState !== "approved" && (
                        <Button size="sm" variant="primary" loading={reviewBusy === c.id} onClick={() => approve(c)}>
                          Approve
                        </Button>
                      )}
                      {c.reviewState !== "rejected" && (
                        <Button size="sm" variant="secondary" onClick={() => setRejecting(c)}>
                          Reject
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => setFormTarget(c)}
                        className="font-menu text-xs tracking-wide text-ele-blue hover:underline"
                      >
                        Edit
                      </button>
                      {c.hasFile && (
                        <a
                          href={api.subcontractors.docDownloadUrl(subId, c.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-menu text-xs tracking-wide text-ele-green hover:underline"
                        >
                          Download
                        </a>
                      )}
                      {c.expiryDate && <RenewalButton subId={subId} docId={c.id} onSent={retry} />}
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteTarget(c);
                          setDeleteError(null);
                        }}
                        className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejecting && (
        <RejectDocumentModal
          subcontractorName=""
          label={`${rejecting.typeLabel}${rejecting.engineerName ? ` for ${rejecting.engineerName}` : ""}`}
          onClose={() => setRejecting(null)}
          onConfirm={async (reason) => {
            await api.subcontractors.rejectCertificate(subId, rejecting.id, reason);
            setNotice(`${rejecting.typeLabel} marked not accepted. The subcontractor sees the reason on the portal.`);
            setRejecting(null);
            retry();
          }}
        />
      )}
      {formTarget && (
        <CertificateFormModal
          subId={subId}
          existing={formTarget === "add" ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onSaved={(isPast) => {
            setFormTarget(null);
            retry();
            setNotice(isPast ? "Saved. That expiry date has already passed, so this certificate shows as expired." : null);
          }}
        />
      )}

      {showUpload && (
        <UploadModal
          subId={subId}
          uploading={uploading}
          setUploading={setUploading}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            retry();
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleteTarget != null}
        title="Delete certificate?"
        message={deleteTarget ? certDeleteMessage(deleteTarget) : ""}
        confirmLabel="Delete"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />
    </Card>
  );
}

function RenewalButton({ subId, docId, onSent }: { subId: string; docId: string; onSent: () => void }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function send() {
    setBusy(true);
    setErr(null);
    try {
      await api.subcontractors.sendDocRenewal(subId, docId);
      setSent(true);
      onSent();
    } catch (e) {
      // leave the button available to retry
      setErr(friendlyApiError(e, "Could not draft the renewal request."));
    } finally {
      setBusy(false);
    }
  }
  if (sent) return <span className="font-menu text-xs uppercase tracking-wide text-ele-green">Renewal request in Gmail Drafts</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button onClick={send} disabled={busy} className="font-menu text-xs tracking-wide text-ele-blue hover:underline disabled:opacity-50">
        {busy ? "Drafting..." : "Draft renewal request"}
      </button>
      {err && (
        <span role="alert" className="text-xs text-ele-danger" style={{ whiteSpace: "normal" }}>
          {err}
        </span>
      )}
    </span>
  );
}

// Add / edit a certificate record. Recording a certificate needs no file (Add certificate); the
// existing multipart Upload document path is preserved separately for attaching one. Past
// expiry dates are allowed (historical records are legitimate for audit), never blocked: a
// non-blocking amber note is shown by the caller via the returned isPast flag.
function CertificateFormModal({
  subId,
  existing,
  onClose,
  onSaved,
}: {
  subId: string;
  existing: SubcontractorCertificate | null;
  onClose: () => void;
  onSaved: (isPast: boolean) => void;
}) {
  // A legacy or uploaded document can carry no structured certType (certType null): seed the
  // select with an explicit UNSET value ("") rather than silently defaulting to the first real
  // option, so opening Edit never pre-selects "Public liability insurance" for a row that has
  // never been classified. "" is only ever the initial value here; a fresh Add certificate
  // always starts on a real type because CERT_TYPE_OPTIONS[0] is used when there is no existing
  // row to read from.
  const isUnclassified = !!existing && existing.certType == null;
  const [certType, setCertType] = useState<SubCertType | "">(existing ? existing.certType ?? "" : CERT_TYPE_OPTIONS[0]!);
  const [name, setName] = useState(existing?.name ?? "");
  const [issuer, setIssuer] = useState(existing?.issuer ?? "");
  const [referenceNumber, setReferenceNumber] = useState(existing?.referenceNumber ?? "");
  const [issuedAt, setIssuedAt] = useState(toDateInputValue(existing?.issuedAt));
  const [expiresAt, setExpiresAt] = useState(toDateInputValue(existing?.expiryDate));
  // The server never returns the stored file link (hasFile is the only signal), so this field
  // always starts blank on Edit. Left blank, it is omitted from the PATCH body entirely, so
  // editing another field never silently clears an already-attached document.
  const [fileUrl, setFileUrl] = useState("");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) {
      setErr("Enter the certificate name.");
      return;
    }
    if (!expiresAt) {
      setErr("Enter the expiry date.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const base = {
        // Only send certType when the office actually holds (or has just chosen) a real value.
        // A row opened on its unset ("") seed must NOT send certType at all: sending it is what
        // overwrites the server's stored docType (see the isUnclassified comment above).
        ...(certType ? { certType } : {}),
        name: name.trim(),
        issuer: issuer.trim(),
        referenceNumber: referenceNumber.trim(),
        issuedAt,
        expiresAt,
        notes: notes.trim(),
      };
      const res = existing
        ? await api.subcontractors.updateCertificate(subId, existing.id, {
            ...base,
            ...(fileUrl.trim() ? { fileUrl: fileUrl.trim() } : {}),
          })
        : await api.subcontractors.createCertificate(subId, {
            ...base,
            fileUrl: fileUrl.trim() || undefined,
          } as SubcontractorCertificateInput);
      onSaved(res.isPast);
    } catch (e) {
      setErr(friendlyApiError(e, "Could not save the certificate."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel={existing ? "Edit certificate" : "Add certificate"} onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <h2 className="mb-3 font-heading text-2xl tracking-wide text-ele-strong">{existing ? "Edit certificate" : "Add certificate"}</h2>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Certificate type</label>
          <select
            value={certType}
            aria-label="Certificate type"
            onChange={(e) => setCertType(e.target.value as SubCertType | "")}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
          >
            {isUnclassified && (
              <option value="">{existing?.typeLabel ? `${existing.typeLabel} (not yet classified)` : "Not yet classified"}</option>
            )}
            {CERT_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>{CERT_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {isUnclassified && (
            <p className="mt-1 text-xs text-ele-body opacity-70">
              This document has not been classified into a certificate type. Leave this as is unless you want to
              reclassify it: choosing a type here replaces the record above.
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Name</label>
          <input
            value={name}
            aria-label="Certificate name"
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Zurich Public Liability 2024-25"
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Issuer (optional)</label>
            <input value={issuer} onChange={(e) => setIssuer(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Reference number (optional)</label>
            <input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Issue date (optional)</label>
            <input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Expiry date</label>
            <input
              type="date"
              value={expiresAt}
              aria-label="Expiry date"
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Document URL (optional)</label>
          <input
            value={fileUrl}
            aria-label="Document URL"
            onChange={(e) => setFileUrl(e.target.value)}
            placeholder="https://"
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong"
          />
          <p className="mt-1 text-xs text-ele-body opacity-70">Uploading a file here is coming in a later release. Use Upload document to attach a file now.</p>
        </div>
        <div>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Notes (optional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
        </div>
      </div>
      {err && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onClose} disabled={busy}>Cancel</Button>
        <Button className={MODAL_ACTION_BTN} onClick={save} disabled={busy}>
          {busy ? "Saving..." : existing ? "Save" : "Add certificate"}
        </Button>
      </div>
    </ModalShell>
  );
}

const DOC_TYPES = [
  "Public Liability Certificate",
  "Employer Liability Certificate",
  "CSCS Card",
  "NVQ Certificate",
  "IPAF Card",
  "AM2 Certificate",
  "City and Guilds Certificate",
  "Manufacturer Training Certificate",
  "Other",
];

function UploadModal({ subId, uploading, setUploading, onClose, onUploaded }: { subId: string; uploading: boolean; setUploading: (b: boolean) => void; onClose: () => void; onUploaded: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState(DOC_TYPES[0]!);
  const [description, setDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setErr("Choose a file to upload.");
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      await api.subcontractors.uploadDoc(subId, file, { docType, description: description || undefined, expiryDate: expiryDate || undefined });
      onUploaded();
    } catch (e) {
      setErr(friendlyApiError(e, "Upload failed."));
    } finally {
      setUploading(false);
    }
  }

  return (
    <ModalShell ariaLabel="Upload document" onClose={() => !uploading && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
        <h2 className="mb-3 font-heading text-2xl tracking-wide text-ele-strong">Upload document</h2>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Document type</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong">
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">Expiry date (optional)</label>
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">File (PDF, JPG, PNG)</label>
            <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" aria-label="Document file" className="w-full text-sm text-ele-body file:mr-3 file:rounded-ele file:border-0 file:bg-ele-green file:px-3 file:py-2 file:font-menu file:text-xs file:uppercase file:text-ele-bg" />
          </div>
        </div>
        {err && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{err}</p>}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onClose}>Cancel</Button>
          <Button className={MODAL_ACTION_BTN} onClick={upload} disabled={uploading}>{uploading ? "Uploading..." : "Upload"}</Button>
        </div>
    </ModalShell>
  );
}

function ProjectsSection({
  projects,
  error,
  onRetry,
}: {
  projects: SubcontractorProjectLink[];
  error?: boolean;
  onRetry?: () => void;
}) {
  return (
    <Card title="Projects">
      {error ? (
        <p role="alert" className="text-sm text-ele-danger">
          Could not load linked projects.{" "}
          <button type="button" onClick={onRetry} className="underline hover:text-ele-strong">Retry</button>
        </p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-ele-body">Not linked to any projects.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {projects.map((p) => (
            <li key={p.linkId}>
              <Link to={`/office/projects/${p.projectId}`} className="text-ele-green hover:underline">{p.projectRef}</Link>
              <span className="text-ele-body"> {p.projectTitle}{p.role ? ` (${p.role})` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Editable({
  label,
  value,
  onChange,
  type = "text",
  linkKind,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  // Session AEH B4: when set, shows the current value below the field as a tappable
  // mailto:/tel: link (ContactValue), alongside the editable input.
  linkKind?: "email" | "phone";
}) {
  return (
    <div>
      <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue">{label}</label>
      <input type={type} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)} className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong" />
      {linkKind && value.trim() && (
        <p className="mt-1">
          <ContactValue value={value} kind={linkKind} className="text-xs text-ele-blue hover:underline" />
        </p>
      )}
    </div>
  );
}
